import { Fuel, PendingTopup, TopupWithState } from '@/src/api/payg/types';
import {
  TOPUP_DETAIL_COPY,
  TOPUP_NOTIFICATION_COPY,
  TOPUP_PROCESSING_COPY,
  TopupCopy,
  TopupDetailState,
  TopupNotificationState,
  TopupProcessingState,
} from './topupCopy';

const pickVariant = <T>(
  variants: { default: T; asc?: T },
  isAsc?: boolean,
): T => (isAsc && variants.asc) || variants.default;

export const getTopupDetailCopy = ({
  state,
  isAsc,
}: {
  state: TopupDetailState;
  isAsc?: boolean;
}): TopupCopy => pickVariant(TOPUP_DETAIL_COPY[state], isAsc);

export const isTopupNotification = (
  topup: PendingTopup,
): topup is TopupWithState<PendingTopup, TopupNotificationState> =>
  topup.state in TOPUP_NOTIFICATION_COPY;

export const getTopupNotificationCopy = ({
  state,
  fuel,
  isAsc,
}: {
  state: TopupNotificationState;
  fuel: Fuel;
  isAsc?: boolean;
}): TopupCopy => {
  const { title, ...copy } = pickVariant(TOPUP_NOTIFICATION_COPY[state], isAsc);

  return { ...copy, title: typeof title === 'function' ? title(fuel) : title };
};

type ProcessingProps = {
  state: TopupProcessingState;
  fuel: Fuel;
  minutes?: number;
};

const getProcessingMessage = ({ state, fuel, minutes }: ProcessingProps) => {
  const { messages } = TOPUP_PROCESSING_COPY;

  if (state === 'AUTO_APPLY_DELAYED') {
    return messages.AUTO_APPLY_DELAYED;
  }
  // Only a gas top-up in progress quotes minutes: electricity balances update
  // straight away and PENDING_PAYMENT has no minutes to quote.
  if (state === 'PROCESSING' && fuel === Fuel.GAS && minutes) {
    return messages.PROCESSING(minutes);
  }
  return messages.PENDING_PAYMENT;
};

export const getTopupProcessingCopy = (props: ProcessingProps): TopupCopy => ({
  title: TOPUP_PROCESSING_COPY.title(props.fuel),
  message: getProcessingMessage(props),
});

/**
 * The detail page has no per-state heading, so its title is read as the opening
 * sentence of the message. Notifications render the title separately.
 */
export const joinTitleAndMessage = ({ title, message }: TopupCopy) =>
  [title, message].filter(Boolean).join('. ');
